import { useState, useEffect, useCallback, useMemo } from 'react';
import { tierConfigApi } from '../api/tierConfig';
import type { BoardTierConfig, NumberMode } from '../types';

/**
 * Hook for fetching board tier configurations
 * Used in board creation UI to show credit costs for each number mode
 */
export function useTierConfig() {
  const [configs, setConfigs] = useState<BoardTierConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfigs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await tierConfigApi.getTierConfigs();
      setConfigs(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tier configurations');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Helper to get credit cost for a specific number mode
  const getCreditCost = useCallback((numberMode: NumberMode | undefined): number => {
    if (!numberMode) return 1; // Default for single
    const config = configs.find(c => c.numberMode === numberMode);
    if (config) return config.creditCost;
    // Fallback defaults if config not found
    switch (numberMode) {
      case 'single': return 1;
      case 'half': return 2;
      case 'quarter': return 4;
      default: return 1;
    }
  }, [configs]);

  // Helper to get display name for a specific number mode
  const getDisplayName = useCallback((numberMode: NumberMode | undefined): string => {
    if (!numberMode) return 'Same for All Periods';
    const config = configs.find(c => c.numberMode === numberMode);
    if (config) return config.displayName;
    // Fallback defaults if config not found
    switch (numberMode) {
      case 'single': return 'Same for All Periods';
      case 'half': return 'Different per Half';
      case 'quarter': return 'Different per Quarter';
      default: return 'Same for All Periods';
    }
  }, [configs]);

  // Helper to get config by number mode
  const getConfig = useCallback((numberMode: NumberMode | undefined): BoardTierConfig | undefined => {
    if (!numberMode) return configs.find(c => c.numberMode === 'single');
    return configs.find(c => c.numberMode === numberMode);
  }, [configs]);

  // Map of number mode to credit cost for easy lookup
  const creditCostMap = useMemo(() => {
    const map: Record<string, number> = {};
    configs.forEach(config => {
      map[config.numberMode] = config.creditCost;
    });
    return map;
  }, [configs]);

  return {
    configs,
    isLoading,
    error,
    refresh: fetchConfigs,
    getCreditCost,
    getDisplayName,
    getConfig,
    creditCostMap,
  };
}
